import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { supabase } from '../../services/supabase';
import { receiptUploadService } from '../../services/receiptUploadService';

// Helper: clamp a value to fit into Postgres numeric(p,s)
function clampAmountForNumeric(value: unknown, p = 12, s = 2): number | null {
  const n = Number(value);
  if (!isFinite(n) || Number.isNaN(n)) return null;
  const max = Math.pow(10, p - s) - Math.pow(10, -s); // e.g. numeric(12,2) -> 10^(10)-0.01
  const min = -max;
  // keep s decimal places
  const fixed = Number(n.toFixed(s));
  if (fixed > max) {
    console.warn(`[clampAmountForNumeric] value ${n} exceeds max ${max}, clamping to ${max}`);
    return max;
  }
  if (fixed < min) {
    console.warn(`[clampAmountForNumeric] value ${n} below min ${min}, clamping to ${min}`);
    return min;
  }
  return fixed;
}

// 类型定义
export interface ExpenseReceipt {
  id: string;
  group_id: string;
  payer_id: string;
  created_by: string;
  title: string;
  description: string | null;
  amount: number;
  currency: string;
  // 新增汇率换算字段
  converted_amount?: number; // 换算为基础货币的金额
  exchange_rate?: number; // 汇率
  exchange_rate_date?: string; // 汇率日期
  category: string;
  expense_date: string;
  receipt_url: string; // 修正：匹配数据库的单个URL字段
  split_method: string;
  status?: 'pending' | 'approved' | 'rejected'; // 可选，因为可能从其他地方添加
  created_at: string;
  updated_at: string;
  // 联表查询的用户信息
  uploader?: {
    id: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

export interface PrivateReceiptParticipant {
  id?: string;
  receipt_id: string;
  group_id: string;
  user_id: string;
  amount: number | null;
  percentage: number | null;
  share: number | null;
  note: string | null;
  profile?: {
    display_name: string | null;
    avatar_url: string | null;
  } | null;
}

export interface PrivateReceipt {
  id: string;
  group_id: string;
  uploader_id: string;
  title: string;
  description?: string | null;
  amount: number;
  currency: string;
  converted_amount?: number | null;
  exchange_rate?: number | null;
  exchange_rate_date?: string | null;
  category?: string | null;
  expense_date?: string | null;
  receipt_url?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  file_type?: string | null;
  split_mode: 'equal' | 'exact' | 'percentage' | 'shares';
  is_manual_rate?: boolean;
  status?: string | null;
  created_at: string;
  updated_at: string;
  uploader?: {
    id: string;
    display_name: string | null;
    avatar_url: string | null;
  } | null;
  members?: PrivateReceiptParticipant[];
}

export interface ReceiptLog {
  id: string;
  receipt_id: string;
  user_id: string;
  action: string;
  old_values: any | null;
  new_values: any | null;
  comment: string | null;
  created_at: string;
  user?: {
    display_name: string | null;
    avatar_url: string | null;
  };
}

export interface CreateReceiptData {
  group_id: string;
  title: string;
  description?: string;
  amount: number;
  currency: string;
  // 新增汇率换算数据
  converted_amount?: number;
  exchange_rate?: number;
  exchange_rate_date?: string;
  is_manual_rate?: boolean; // 标记是否为手动汇率
  category: string;
  expense_date: string;
  file?: {
    uri: string;
    name: string;
    type: string;
    size: number;
  };
}

export interface PrivateSplitParticipantPayload {
  user_id: string;
  amount?: number | null;
  percentage?: number | null;
  share?: number | null;
  note?: string | null;
}

export interface CreatePrivateReceiptData extends CreateReceiptData {
  split_mode: 'equal' | 'exact' | 'percentage' | 'shares';
  participants: PrivateSplitParticipantPayload[];
}

export interface ApprovalData {
  receipt_id: string;
  action: 'approve' | 'reject';
  comment?: string;
}

export interface ExpenseReceiptsState {
  receipts: ExpenseReceipt[];
  currentReceipt: ExpenseReceipt | null;
  logs: ReceiptLog[];
  loading: boolean;
  uploadLoading: boolean;
  error: string | null;
  uploadProgress: number;
  privateReceipts: PrivateReceipt[];
  privateReceiptsLoading: boolean;
  privateReceiptsError: string | null;
  privateReceiptsFetchedGroupId: string | null;
}

// 初始状态
const initialState: ExpenseReceiptsState = {
  receipts: [],
  currentReceipt: null,
  logs: [],
  loading: false,
  uploadLoading: false,
  error: null,
  uploadProgress: 0,
  privateReceipts: [],
  privateReceiptsLoading: false,
  privateReceiptsError: null,
  privateReceiptsFetchedGroupId: null,
};

// 异步操作：获取群组收据
export const fetchGroupReceipts = createAsyncThunk(
  'expenseReceipts/fetchGroupReceipts',
  async (groupId: string, { rejectWithValue }) => {
    try {
      // 1. 获取收据数据
      const { data: receiptsData, error } = await supabase
        .from('expense_receipts')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!receiptsData || receiptsData.length === 0) {
        return [];
      }

      // 2. 获取所有相关用户的信息
      const userIds = [...new Set(receiptsData.map(receipt => receipt.uploader_id))];
      
      let usersData: Array<{id: string; display_name: string | null; avatar_url: string | null}> = [];
      if (userIds.length > 0) {
        const { data: users, error: usersError } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .in('id', userIds);

        if (usersError) {
          console.warn('获取用户信息失败:', usersError);
          // 即使获取用户信息失败，也创建一个基本的用户信息
          usersData = userIds.map(id => ({
            id,
            display_name: null,
            avatar_url: null
          }));
        } else {
          usersData = users || [];
          
          // 确保所有需要的用户ID都有对应的用户信息
          userIds.forEach(id => {
            if (!usersData.find(user => user.id === id)) {
              usersData.push({
                id,
                display_name: null,
                avatar_url: null
              });
            }
          });
        }
      }

      // 3. 合并数据
      const receiptsWithUsers = receiptsData.map(receipt => {
        const uploader = usersData?.find(user => user.id === receipt.uploader_id);
        return {
          ...receipt,
          uploader: uploader || {
            id: receipt.uploader_id,
            display_name: null,
            avatar_url: null
          }
        };
      });

      return receiptsWithUsers as ExpenseReceipt[];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchGroupPrivateReceipts = createAsyncThunk(
  'expenseReceipts/fetchGroupPrivateReceipts',
  async (groupId: string, { rejectWithValue }) => {
    try {
      const { data: receiptsData, error } = await supabase
        .from('group_private_receipts')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!receiptsData || receiptsData.length === 0) {
        return { groupId, receipts: [] as PrivateReceipt[] };
      }

      const receiptIds = receiptsData.map(r => r.id);

      const { data: memberRows, error: membersError } = await supabase
        .from('group_private_receipt_members')
        .select('*')
        .in('receipt_id', receiptIds);

      if (membersError) throw membersError;

      const uploaderIds = receiptsData.map(r => r.uploader_id);
      const participantIds = (memberRows || []).map(row => row.user_id);
      const uniqueUserIds = Array.from(new Set([...uploaderIds, ...participantIds]));

      let profiles: Array<{ id: string; display_name: string | null; avatar_url: string | null }> = [];
      if (uniqueUserIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .in('id', uniqueUserIds);

        if (profilesError) {
          console.warn('获取私账用户信息失败:', profilesError);
        } else if (profilesData) {
          profiles = profilesData;
        }
      }

      const profileMap = new Map<string, { display_name: string | null; avatar_url: string | null }>();
      profiles.forEach(profile => {
        profileMap.set(profile.id, {
          display_name: profile.display_name,
          avatar_url: profile.avatar_url,
        });
      });

      const membersByReceipt = new Map<string, PrivateReceiptParticipant[]>();
      (memberRows || []).forEach(row => {
        const list = membersByReceipt.get(row.receipt_id) || [];
        list.push({
          ...row,
          profile: profileMap.get(row.user_id) || null,
        });
        membersByReceipt.set(row.receipt_id, list);
      });

      const receipts: PrivateReceipt[] = receiptsData.map(record => ({
        ...record,
        status: record.status && record.status !== 'pending' ? record.status : 'approved',
        uploader: record.uploader_id
          ? {
              id: record.uploader_id,
              display_name: profileMap.get(record.uploader_id)?.display_name || null,
              avatar_url: profileMap.get(record.uploader_id)?.avatar_url || null,
            }
          : null,
        members: membersByReceipt.get(record.id) || [],
      }));

      return { groupId, receipts };
    } catch (error: any) {
      return rejectWithValue(error.message || '加载私账失败');
    }
  }
);

// 异步操作：获取收据详情
export const fetchReceiptDetail = createAsyncThunk(
  'expenseReceipts/fetchReceiptDetail',
  async (receiptId: string, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('expense_receipts')
        .select('*')
        .eq('id', receiptId)
        .single();

      if (error) throw error;
      return data as ExpenseReceipt;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 异步操作：获取收据日志
export const fetchReceiptLogs = createAsyncThunk(
  'expenseReceipts/fetchReceiptLogs',
  async (receiptId: string, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('expense_receipt_logs')
        .select('*')
        .eq('receipt_id', receiptId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ReceiptLog[];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 异步操作：上传收据
export const uploadReceipt = createAsyncThunk(
  'expenseReceipts/uploadReceipt',
  async (receiptData: CreateReceiptData, { rejectWithValue }) => {
    try {
      console.log('🔧 开始上传收据...');
      console.log('📁 收据数据:', {
        group_id: receiptData.group_id,
        title: receiptData.title,
        amount: receiptData.amount,
        category: receiptData.category,
        file: receiptData.file ? {
          name: receiptData.file.name,
          type: receiptData.file.type,
          size: receiptData.file.size,
        } : null
      });

      // 获取当前用户信息
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('用户未登录');
      }
      console.log('✅ 用户认证正常:', user.email);

      // 1. 上传文件到 Cloudflare R2（如果有文件的话）
      let publicUrl: string | null = null;
      
      if (receiptData.file) {
        try {
          console.log('📤 开始上传收据到 Cloudflare R2...');
          console.log('📋 文件信息:', {
            name: receiptData.file.name,
            type: receiptData.file.type,
            size: receiptData.file.size,
            uri: receiptData.file.uri,
          });

          // 使用 Cloudflare R2 上传服务
          const uploadResult = await receiptUploadService.uploadReceipt(
            receiptData.file,
            user.id,
            receiptData.group_id
          );

          if (!uploadResult.success) {
            console.log('❌ Cloudflare R2 上传失败:', uploadResult.error);
            throw new Error(`文件上传失败: ${uploadResult.error}`);
          }
          
          console.log('✅ Cloudflare R2 上传成功:', uploadResult.data);
          publicUrl = uploadResult.data!.url;

        } catch (fetchError: any) {
          console.log('❌ 文件处理失败:', fetchError);
          throw new Error(`文件处理失败: ${fetchError.message}`);
        }
      } else {
        console.log('📝 没有文件，跳过文件上传步骤');
      }

      // 3. 保存收据记录到数据库
      console.log('👤 当前用户:', user.id);

      const insertData = {
        group_id: receiptData.group_id,
        uploader_id: user.id,
        title: receiptData.title,
        description: receiptData.description,
  // sanitize amount to fit numeric(12,2)
  amount: clampAmountForNumeric(receiptData.amount, 12, 2),
        currency: receiptData.currency,
        // 新增汇率换算字段
  converted_amount: clampAmountForNumeric(receiptData.converted_amount, 12, 2),
        exchange_rate: receiptData.exchange_rate,
        exchange_rate_date: receiptData.exchange_rate_date,
        category: receiptData.category,
        expense_date: receiptData.expense_date,
        receipt_url: publicUrl,
        file_name: receiptData.file?.name || null,
        file_size: receiptData.file?.size || null,
        file_type: receiptData.file?.type || null,
  status: 'approved',
      };

  console.log('💾 准备插入数据库 (sanitized):', insertData);

      const { data: receiptRecord, error: dbError } = await supabase
        .from('expense_receipts')
        .insert(insertData)
        .select('*')
        .single();

      if (dbError) {
        console.log('❌ 数据库插入失败:', dbError);
        throw dbError;
      }

      console.log('✅ 收据记录创建成功:', receiptRecord);

      // 4. enrich the returned record with uploader info so UI can show display_name immediately
      const uploaderInfo = {
        id: user.id,
        display_name: (user.user_metadata && (user.user_metadata.full_name || user.user_metadata.display_name)) || (user.email ? user.email.split('@')[0] : null),
        avatar_url: (user.user_metadata && user.user_metadata.avatar_url) || null,
      };

      const receiptWithUploader = {
        ...receiptRecord,
        uploader: uploaderInfo,
      } as ExpenseReceipt;

      // 5. 触发 OCR 识别 (后续实现)
      // dispatch(triggerOCR(receiptRecord.id));

      return receiptWithUploader;
    } catch (error: any) {
      console.log('❌ uploadReceipt 总体错误:', error);
      return rejectWithValue(error.message || '上传收据失败');
    }
  }
);

export const uploadPrivateReceipt = createAsyncThunk(
  'expenseReceipts/uploadPrivateReceipt',
  async (receiptData: CreatePrivateReceiptData, { rejectWithValue }) => {
    try {
      console.log('🔧 开始上传私账收据...');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('用户未登录');
      }

      let publicUrl: string | null = null;

      if (receiptData.file) {
        console.log('📤 上传私账收据文件...');
        const uploadResult = await receiptUploadService.uploadReceipt(
          receiptData.file,
          user.id,
          receiptData.group_id
        );

        if (!uploadResult.success) {
          console.log('❌ 私账收据文件上传失败:', uploadResult.error);
          throw new Error(`文件上传失败: ${uploadResult.error}`);
        }

        publicUrl = uploadResult.data!.url;
      }

      const insertData = {
        group_id: receiptData.group_id,
        uploader_id: user.id,
        title: receiptData.title,
        description: receiptData.description,
        amount: clampAmountForNumeric(receiptData.amount, 12, 2),
        currency: receiptData.currency,
        converted_amount: clampAmountForNumeric(receiptData.converted_amount, 12, 2),
        exchange_rate: receiptData.exchange_rate,
        exchange_rate_date: receiptData.exchange_rate_date,
        category: receiptData.category,
        expense_date: receiptData.expense_date,
        receipt_url: publicUrl,
        file_name: receiptData.file?.name || null,
        file_size: receiptData.file?.size || null,
        file_type: receiptData.file?.type || null,
        split_mode: receiptData.split_mode,
        is_manual_rate: receiptData.is_manual_rate ?? false,
        status: 'approved',
      };

      const { data: receiptRecord, error: dbError } = await supabase
        .from('group_private_receipts')
        .insert(insertData)
        .select('*')
        .single();

      if (dbError) {
        console.log('❌ 私账收据数据库插入失败:', dbError);
        throw dbError;
      }

      let memberRows: PrivateReceiptParticipant[] = [];
      if (receiptData.participants && receiptData.participants.length > 0) {
        memberRows = receiptData.participants.map(participant => {
          const hasAmount = participant.amount !== null && participant.amount !== undefined && Number.isFinite(participant.amount as number);
          const amount = hasAmount ? clampAmountForNumeric(participant.amount, 12, 2) : null;
          const hasPercentage = participant.percentage !== null && participant.percentage !== undefined && Number.isFinite(participant.percentage as number);
          const percentage = hasPercentage ? Number((participant.percentage as number).toFixed(3)) : null;
          const hasShare = participant.share !== null && participant.share !== undefined && Number.isFinite(participant.share as number);
          const share = hasShare ? Number((participant.share as number).toFixed(4)) : null;

          return {
            receipt_id: receiptRecord.id,
            group_id: receiptData.group_id,
            user_id: participant.user_id,
            amount,
            percentage,
            share,
            note: participant.note ?? null,
          } as PrivateReceiptParticipant;
        });

        const { data: insertedMembers, error: membersError } = await supabase
          .from('group_private_receipt_members')
          .insert(memberRows)
          .select('*');

        if (membersError) {
          console.log('❌ 私账分摊明细插入失败，回滚主记录:', membersError);
          await supabase
            .from('group_private_receipts')
            .delete()
            .eq('id', receiptRecord.id);
          throw membersError;
        }

        if (insertedMembers) {
          memberRows = insertedMembers as PrivateReceiptParticipant[];
        }
      }

      const uniqueUserIds = new Set<string>([user.id]);
      memberRows.forEach(row => {
        if (row.user_id) uniqueUserIds.add(row.user_id);
      });

      const profileMap = new Map<string, { display_name: string | null; avatar_url: string | null }>();
      if (uniqueUserIds.size > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .in('id', Array.from(uniqueUserIds));

        if (profilesError) {
          console.warn('获取私账用户信息失败:', profilesError);
        } else if (profilesData) {
          profilesData.forEach(profile => {
            profileMap.set(profile.id, {
              display_name: profile.display_name,
              avatar_url: profile.avatar_url,
            });
          });
        }
      }

      const members: PrivateReceiptParticipant[] = memberRows.map(row => ({
        ...row,
        profile: profileMap.get(row.user_id) || null,
      }));

      const uploaderProfile = profileMap.get(user.id);

      const privateReceipt: PrivateReceipt = {
        ...receiptRecord,
        status: 'approved',
        uploader: {
          id: user.id,
          display_name: uploaderProfile?.display_name || (user.user_metadata as any)?.full_name || null,
          avatar_url: uploaderProfile?.avatar_url || null,
        },
        members,
      };

      return privateReceipt;
    } catch (error: any) {
      console.log('❌ uploadPrivateReceipt 总体错误:', error);
      return rejectWithValue(error.message || '上传私账失败');
    }
  }
);

// 异步操作：审批收据
export const approveReceipt = createAsyncThunk(
  'expenseReceipts/approveReceipt',
  async (approvalData: ApprovalData, { rejectWithValue }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('用户未登录');

      const updateData: any = {
        approved_by: user.id,
        approved_at: new Date().toISOString(),
      };

      if (approvalData.action === 'approve') {
        updateData.status = 'approved';
      } else {
        updateData.status = 'rejected';
        updateData.rejection_reason = approvalData.comment;
      }

      const { data, error } = await supabase
        .from('expense_receipts')
        .update(updateData)
        .eq('id', approvalData.receipt_id)
        .select('*')
        .single();

      if (error) throw error;
      return data as ExpenseReceipt;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 异步操作：更新收据信息
export const updateReceipt = createAsyncThunk(
  'expenseReceipts/updateReceipt',
  async ({ 
    receiptId, 
    updates 
  }: { 
    receiptId: string; 
    updates: Partial<ExpenseReceipt> 
  }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('expense_receipts')
        .update(updates)
        .eq('id', receiptId)
        .select('*')
        .single();

      if (error) throw error;
      return data as ExpenseReceipt;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 异步操作：删除收据
export const deleteReceipt = createAsyncThunk(
  'expenseReceipts/deleteReceipt',
  async (receiptId: string, { rejectWithValue }) => {
    try {
      // 1. 获取收据信息（包含 uploader_id 与 file_name）
      const { data: receipt, error: fetchError } = await supabase
        .from('expense_receipts')
        .select('receipt_url, group_id, file_name, uploader_id')
        .eq('id', receiptId)
        .single();

      if (fetchError) throw fetchError;

      // 2. 从 Cloudflare R2 删除文件（如果有文件URL）
      if (receipt.receipt_url) {
        console.log('🗑️ 开始从 Cloudflare R2 删除收据文件:', receipt.receipt_url);
        
        // 从 CDN URL 提取文件键
        const fileKey = receiptUploadService.extractKeyFromUrl(receipt.receipt_url);
        
        if (fileKey && receipt.uploader_id) {
          const deleteSuccess = await receiptUploadService.deleteReceipt(fileKey, receipt.uploader_id);
          
          if (!deleteSuccess) {
            console.warn('⚠️ Cloudflare R2 文件删除失败，但继续删除数据库记录');
            // 不抛出错误，继续删除数据库记录
          } else {
            console.log('✅ Cloudflare R2 文件删除成功');
          }
        } else {
          console.warn('[deleteReceipt] 无法解析文件键或缺少用户ID', receiptId, receipt);
        }
      } else {
        console.log('ℹ️ 收据没有文件URL，跳过文件删除');
      }

      // 4. 删除数据库记录
      const { error: deleteError } = await supabase
        .from('expense_receipts')
        .delete()
        .eq('id', receiptId);

      if (deleteError) throw deleteError;

      return receiptId;
    } catch (error: any) {
      console.error('[deleteReceipt] error:', error);
      return rejectWithValue(error?.message || String(error));
    }
  }
);

// Redux Slice
const expenseReceiptsSlice = createSlice({
  name: 'expenseReceipts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentReceipt: (state) => {
      state.currentReceipt = null;
    },
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchGroupReceipts
      .addCase(fetchGroupReceipts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupReceipts.fulfilled, (state, action) => {
        state.loading = false;
        state.receipts = action.payload;
      })
      .addCase(fetchGroupReceipts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // fetchGroupPrivateReceipts
      .addCase(fetchGroupPrivateReceipts.pending, (state) => {
        state.privateReceiptsLoading = true;
        state.privateReceiptsError = null;
      })
      .addCase(fetchGroupPrivateReceipts.fulfilled, (state, action) => {
        state.privateReceiptsLoading = false;
        state.privateReceiptsError = null;
        state.privateReceiptsFetchedGroupId = action.payload.groupId;
        state.privateReceipts = action.payload.receipts;
      })
      .addCase(fetchGroupPrivateReceipts.rejected, (state, action) => {
        state.privateReceiptsLoading = false;
        state.privateReceiptsError = action.payload as string;
      })
      // fetchReceiptDetail
      .addCase(fetchReceiptDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReceiptDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReceipt = action.payload;
      })
      .addCase(fetchReceiptDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // fetchReceiptLogs
      .addCase(fetchReceiptLogs.fulfilled, (state, action) => {
        state.logs = action.payload;
      })
      // uploadReceipt
      .addCase(uploadReceipt.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadReceipt.fulfilled, (state, action) => {
        state.uploadLoading = false;
        state.uploadProgress = 100;
        state.receipts.unshift(action.payload);
      })
      .addCase(uploadReceipt.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload as string;
        state.uploadProgress = 0;
      })
      // uploadPrivateReceipt
      .addCase(uploadPrivateReceipt.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadPrivateReceipt.fulfilled, (state, action: PayloadAction<PrivateReceipt | undefined>) => {
        state.uploadLoading = false;
        state.uploadProgress = 100;
        if (action.payload) {
          state.privateReceipts.unshift(action.payload);
        }
      })
      .addCase(uploadPrivateReceipt.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload as string;
        state.uploadProgress = 0;
      })
      // approveReceipt
      .addCase(approveReceipt.fulfilled, (state, action) => {
        const index = state.receipts.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.receipts[index] = action.payload;
        }
        if (state.currentReceipt?.id === action.payload.id) {
          state.currentReceipt = action.payload;
        }
      })
      // updateReceipt
      .addCase(updateReceipt.fulfilled, (state, action) => {
        const index = state.receipts.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.receipts[index] = action.payload;
        }
        if (state.currentReceipt?.id === action.payload.id) {
          state.currentReceipt = action.payload;
        }
      })
      // deleteReceipt
      .addCase(deleteReceipt.fulfilled, (state, action) => {
        state.receipts = state.receipts.filter(r => r.id !== action.payload);
        if (state.currentReceipt?.id === action.payload) {
          state.currentReceipt = null;
        }
      });
  },
});

export const { clearError, clearCurrentReceipt, setUploadProgress } = expenseReceiptsSlice.actions;
export default expenseReceiptsSlice.reducer;
